import { useState, useEffect } from "react";
import { ChatIcon } from "@/components/icons";
import { Modal } from "@/components/ui/Modal";
import { useAppStore } from "@/core/store/useAppStore";
import { apiClient } from "@/services/apiClient";

interface Note {
  id: string;
  content: string;
  createdAt: string;
}

interface PostNotesModalProps {
  postId: string;
  notesCount: number;
  isOpen: boolean;
  onClose: () => void;
}

export function PostNotesModal({ postId, notesCount, isOpen, onClose }: PostNotesModalProps) {
  const { selectedClient } = useAppStore();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && selectedClient?.id) {
      loadNotes();
    }
  }, [isOpen, postId, selectedClient?.id]);

  const loadNotes = async () => {
    if (!selectedClient?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<{ success: boolean; notes: Note[] }>(
        `/client/${selectedClient.id}/posts/${postId}/notes`,
      );
      setNotes(data.notes || []);
    } catch (err) {
      console.error("Failed to load notes:", err);
      setError("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={
        <div className="flex items-center gap-2">
          <ChatIcon className="w-5 h-5 text-amber-600" />
          <span className="text-lg font-semibold text-gray-900">
            Client Notes ({notesCount})
          </span>
        </div>
      }
    >
      <div className="p-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500 text-center py-4">{error}</p>
        )}

        {!loading && !error && notes.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No notes yet</p>
        )}

        {notes.map((note) => (
          <div key={note.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
            {note.createdAt && (
              <p className="text-xs text-gray-400 mt-2">
                {new Date(note.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
